// src/components/admin/TeamsTab.tsx
import React, { useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import {
  useAdminTeams,
  useAdminTeam,
  useCreateTeam,
  useUpdateTeam,
  useDeleteTeam,
  useRemoveTeamMember,
} from "../../hooks/useAdminData";
import { useUserRole } from "../../hooks/useUserRole";
import CreateEntityDialog, { CreateEntityDialogProps } from "./CreateEntityDialog";
import AdminSearchHeader from "./AdminSearchHeader";
import AdminSnackbar, { AdminSnackState } from "./AdminSnackbar";

type CreateTeamDialogProps = Omit<CreateEntityDialogProps, "title" | "nameLabel">;

const CreateTeamDialog: React.FC<CreateTeamDialogProps> = (props) => (
  <CreateEntityDialog
    {...props}
    title="Create Team"
    nameLabel="Team Name"
  />
);

const TeamsTab: React.FC = () => {
  const { isSuper, isAdmin } = useUserRole();
  const { data: teams, isLoading, error } = useAdminTeams();
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const deleteTeam = useDeleteTeam();
  const removeMember = useRemoveTeamMember();

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedTeamId, setExpandedTeamId] = useState<number | null>(null);
  const [editingTeam, setEditingTeam] = useState<{ id: number; name: string; description: string } | null>(null);
  const [snack, setSnack] = useState<AdminSnackState | null>(null);

  const { data: expandedTeamData } = useAdminTeam(expandedTeamId);

  const filtered = useMemo(() => {
    if (!teams) return [];
    const q = search.toLowerCase();
    return teams.filter(
      (t) => t.name.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)
    );
  }, [teams, search]);

  const handleCreate = async (name: string, description: string) => {
    try {
      await createTeam.mutateAsync({ name, description });
      setSnack({ msg: "Team created", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to create team", severity: "error" });
    }
  };

  const handleUpdate = async () => {
    if (!editingTeam) return;
    try {
      await updateTeam.mutateAsync({
        teamId: editingTeam.id,
        name: editingTeam.name,
        description: editingTeam.description,
      });
      setEditingTeam(null);
      setSnack({ msg: "Team updated", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to update team", severity: "error" });
    }
  };

  const handleDelete = async (teamId: number, teamName: string) => {
    if (!globalThis.confirm(`Delete team "${teamName}"? Members will be unassigned.`)) return;
    try {
      await deleteTeam.mutateAsync(teamId);
      if (expandedTeamId === teamId) setExpandedTeamId(null);
      setSnack({ msg: "Team deleted", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to delete team", severity: "error" });
    }
  };

  const handleRemoveMember = async (teamId: number, clerkUserId: string) => {
    try {
      await removeMember.mutateAsync({ teamId, clerkUserId });
      setSnack({ msg: "Member removed", severity: "success" });
    } catch {
      setSnack({ msg: "Failed to remove member", severity: "error" });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} sx={{ color: "#EF2D3E" }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load teams. Check your permissions.</Alert>;
  }

  return (
    <Box>
      <AdminSearchHeader
        title="Teams"
        count={filtered.length}
        searchPlaceholder="Search teams..."
        search={search}
        onSearchChange={setSearch}
        showCreateButton={isAdmin}
        createLabel="New Team"
        onCreateClick={() => setCreateOpen(true)}
      />

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#F9FAFB" }}>
              <TableCell sx={{ fontWeight: 600, width: 40 }} />
              <TableCell sx={{ fontWeight: 600 }}>Team Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }}>Members</TableCell>
              {isAdmin && <TableCell sx={{ fontWeight: 600, width: 100 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((team) => (
              <React.Fragment key={team.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => setExpandedTeamId(expandedTeamId === team.id ? null : team.id)}
                    >
                      {expandedTeamId === team.id ? (
                        <ExpandLessIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {editingTeam?.id === team.id ? (
                      <TextField
                        size="small"
                        value={editingTeam.name}
                        onChange={(e) => setEditingTeam({ ...editingTeam, name: e.target.value })}
                        onBlur={handleUpdate}
                        onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
                        autoFocus
                        sx={{ width: 200 }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>{team.name}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingTeam?.id === team.id ? (
                      <TextField
                        size="small"
                        value={editingTeam.description}
                        onChange={(e) => setEditingTeam({ ...editingTeam, description: e.target.value })}
                        onBlur={handleUpdate}
                        sx={{ width: 300 }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: "0.8rem", color: "#6B7280" }}>
                        {team.description || "—"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${team.member_count} members`}
                      size="small"
                      sx={{ bgcolor: "#F3F4F6", fontSize: "0.75rem" }}
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Edit team">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setEditingTeam({
                                id: team.id,
                                name: team.name,
                                description: team.description || "",
                              })
                            }
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        {isSuper && (
                          <Tooltip title="Delete team">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(team.id, team.name)}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>

                {/* Expanded member list */}
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4} sx={{ p: 0, border: "none" }}>
                    <Collapse in={expandedTeamId === team.id} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, pl: 6, bgcolor: "#FAFBFC" }}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 1 }}>
                          Members
                        </Typography>
                        {expandedTeamData?.members && expandedTeamData.members.length > 0 ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {expandedTeamData.members.map((m) => (
                              <Box
                                key={m.clerk_user_id}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  py: 0.5,
                                  px: 1,
                                  borderRadius: 1,
                                  "&:hover": { bgcolor: "#F0F0F0" },
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Avatar sx={{ width: 24, height: 24, fontSize: "0.7rem" }}>
                                    {(m.first_name?.[0] || m.email?.[0] || "?").toUpperCase()}
                                  </Avatar>
                                  <Typography sx={{ fontSize: "0.8rem" }}>
                                    {m.first_name} {m.last_name}
                                    {m.email && (
                                      <Typography component="span" sx={{ color: "#9CA3AF", ml: 1, fontSize: "0.75rem" }}>
                                        ({m.email})
                                      </Typography>
                                    )}
                                  </Typography>
                                </Box>
                                {isSuper && (
                                  <Tooltip title="Remove from team">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveMember(team.id, m.clerk_user_id)}
                                    >
                                      <PersonRemoveIcon sx={{ fontSize: 16, color: "#EF4444" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.8rem", color: "#9CA3AF" }}>
                            No members yet. Assign users from the Users tab.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No teams found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateTeamDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        isLoading={createTeam.isPending}
      />

      <AdminSnackbar snack={snack} onClose={() => setSnack(null)} />
    </Box>
  );
};

export default TeamsTab;
